import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { computeParts, formatClock } from "@/lib/timer";
import { Play, Pause, RotateCcw, Plus, Minus, Flag, ArrowRightToLine } from "lucide-react";

export default function ScoreboardTab({ snapshot }) {
  const fields = snapshot.fields || [];
  const lives = snapshot.lives || [];
  const [fieldId, setFieldId] = useState(fields[0]?.id || null);
  const live = lives.find((l) => l.field_id === fieldId) || lives[0] || snapshot.live;
  const teams = snapshot.teams || [];
  const matches = snapshot.matches || [];
  const fieldName = (fields.find((f) => f.id === fieldId) || {}).name;
  const fieldMatches = fieldName ? matches.filter((m) => m.field === fieldName) : matches;

  const [t1, setT1] = useState("");
  const [t2, setT2] = useState("");
  const [matchId, setMatchId] = useState("");
  const [timerMode, setTimerMode] = useState(live.timer.mode);
  const settings = snapshot.settings;
  const showTimer = settings.show_timer !== false;
  const [h1Min, setH1Min] = useState(Math.round((live.timer.duration1 || (settings.half1_duration || 7) * 60) / 60));
  const [h2Min, setH2Min] = useState(Math.round((live.timer.duration2 || (settings.half2_duration || 7) * 60) / 60));
  const [injury, setInjury] = useState(live.timer.injury || 0);
  const [, tick] = useState(0);
  const autoRef = useRef(false);

  useEffect(() => {
    const i = setInterval(() => tick((x) => x + 1), 250);
    return () => clearInterval(i);
  }, []);

  useEffect(() => {
    setT1(live.team1_name);
    setT2(live.team2_name);
    setTimerMode(live.timer.mode);
    setH1Min(Math.round((live.timer.duration1 || (settings.half1_duration || 7) * 60) / 60));
    setH2Min(Math.round((live.timer.duration2 || (settings.half2_duration || 7) * 60) / 60));
    setInjury(live.timer.injury || 0);
    // eslint-disable-next-line
  }, [live.team1_name, live.team2_name, live.timer.mode, live.timer.duration1, live.timer.duration2, live.timer.injury]);

  // auto end of half when countdown + optional injury elapsed
  useEffect(() => {
    const i = setInterval(() => {
      const t = live.timer;
      if (t.mode === "countdown" && t.running && (t.phase || "playing") === "playing") {
        const p = computeParts(t);
        if (p.main === 0 && p.injury >= (t.injury || 0) * 60) {
          if (!autoRef.current) { autoRef.current = true; api.post("/live/timer", { action: "half_end" }).catch(() => {}); }
        } else {
          autoRef.current = false;
        }
      } else {
        autoRef.current = false;
      }
    }, 500);
    return () => clearInterval(i);
  }, [live.timer]);

  const parts = computeParts(live.timer);
  const clock = formatClock(parts.main);
  const half = live.timer.half || 1;
  const phase = live.timer.phase || "playing";

  const call = async (fn) => {
    try {
      await fn();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const applyPairing = () =>
    call(async () => {
      const team1 = teams.find((t) => t.name === t1);
      const team2 = teams.find((t) => t.name === t2);
      await api.put("/live/setup", {
        match_id: matchId || null,
        team1_id: team1?.id || null,
        team2_id: team2?.id || null,
        team1_name: t1,
        team2_name: t2,
        team1_logo: team1?.logo || null,
        team2_logo: team2?.logo || null,
        team1_color: team1?.color || null,
        team2_color: team2?.color || null,
        team1_short: team1?.short || null,
        team2_short: team2?.short || null,
        timer_mode: timerMode,
        timer_duration: 0,
        half1_duration: h1Min,
        half2_duration: h2Min,
        field_id: fieldId,
      });
      toast.success("Spielpaarung aktiv");
    });

  const score = (team, delta) => call(() => api.post("/live/score", { team, delta, field_id: fieldId }));
  const card = (team, card_type, delta) => call(() => api.post("/live/card", { team, card_type, delta, field_id: fieldId }));
  const timer = (action) => call(() => api.post("/live/timer", { action, field_id: fieldId }));
  const configTimer = () =>
    call(async () => {
      await api.post("/live/timer", { action: "config", mode: timerMode, duration1: h1Min * 60, duration2: h2Min * 60, duration: timerMode === "countdown" ? h1Min * 60 : 0, field_id: fieldId });
      toast.success("Uhr konfiguriert");
    });
  const halfEnd = () => call(() => api.post("/live/timer", { action: "half_end", field_id: fieldId }));
  const halfStart = () => call(() => api.post("/live/timer", { action: "half_start", field_id: fieldId }));
  const saveInjury = () => call(() => api.post("/live/injury", { injury: Number(injury), field_id: fieldId }));
  const endMatch = () => call(async () => { await api.post("/live/end", { field_id: fieldId }); toast.success("Spielende gesetzt"); });
  const pushTable = () => call(async () => { await api.post("/live/push-to-table", { field_id: fieldId }); toast.success("In Tabelle übernommen"); });
  const toggleShow = () => call(() => api.post("/live/show", { field_id: fieldId, manual: live.manual === "show" ? "hide" : "show" }));
  const setPhase = (phase) => call(() => api.post("/live/timer", { action: "set_phase", phase, field_id: fieldId }));

  // keyboard shortcuts
  useEffect(() => {
    const onKey = (e) => {
      if (["INPUT", "SELECT", "TEXTAREA"].includes(e.target.tagName)) return;
      if (e.code === "Space") { e.preventDefault(); timer(live.timer.running ? "pause" : "start"); }
      if (e.key === "q") score("team1", 1);
      if (e.key === "a") score("team1", -1);
      if (e.key === "p") score("team2", 1);
      if (e.key === "l") score("team2", -1);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
    // eslint-disable-next-line
  }, [live.timer.running]);

  return (
    <div className="space-y-6">
      {/* Field selector + live toggle */}
      <section className="bg-[#121620] border border-[#1E2638] rounded-2xl p-4 flex flex-wrap items-center gap-3">
        {fields.length > 0 ? (
          <div className="flex flex-wrap gap-2" data-testid="field-tabs">
            {fields.map((f) => (
              <button key={f.id} onClick={() => setFieldId(f.id)} data-testid={`field-tab-${f.id}`}
                className={`font-display font-bold uppercase tracking-wider text-sm px-4 py-2 rounded-lg border transition-colors ${fieldId === f.id ? "text-black" : "text-white border-[#1E2638]"}`}
                style={fieldId === f.id ? { background: f.color || "#00F0FF", borderColor: f.color || "#00F0FF" } : {}}>
                {f.name}
              </button>
            ))}
          </div>
        ) : (
          <span className="font-display uppercase tracking-wider text-sm text-[#8A99AD]">Anzeigetafel</span>
        )}
        <button onClick={toggleShow} data-testid="live-toggle-btn"
          className={`ml-auto flex items-center gap-2 font-display font-bold uppercase tracking-wider text-sm px-4 py-2 rounded-lg transition-colors ${live.manual === "hide" ? "bg-[#121620] border border-[#1E2638] text-[#8A99AD]" : "bg-[#FF2A55] text-white live-dot"}`}>
          <span className="w-3 h-3 rounded-full bg-current" /> {live.manual === "hide" ? "Auf Display zeigen" : "Live"}
        </button>
      </section>

      <section className="bg-[#121620] border border-[#1E2638] rounded-2xl p-6">
        <h2 className="font-display font-bold uppercase tracking-wider text-lg mb-4">Spielpaarung</h2>
        <div className="flex flex-wrap items-end gap-3">
          <Field label="Aus Spielplan">
            <select value={matchId} onChange={(e) => {
              setMatchId(e.target.value);
              const m = matches.find((x) => x.id === e.target.value);
              if (m) { setT1(m.team1_name); setT2(m.team2_name); }
            }} data-testid="pairing-match-select" className="input">
              <option value="">— manuell —</option>
              {fieldMatches.map((m) => <option key={m.id} value={m.id}>{m.team1_name} vs {m.team2_name}</option>)}
            </select>
          </Field>
          <Field label="Team 1">
            <select value={t1} onChange={(e) => setT1(e.target.value)} data-testid="pairing-team1" className="input">
              {teams.map((t) => <option key={t.id}>{t.name}</option>)}
              {!teams.find((t) => t.name === t1) && <option>{t1}</option>}
            </select>
          </Field>
          <span className="pb-2 text-[#8A99AD] font-display uppercase">vs</span>
          <Field label="Team 2">
            <select value={t2} onChange={(e) => setT2(e.target.value)} data-testid="pairing-team2" className="input">
              {teams.map((t) => <option key={t.id}>{t.name}</option>)}
              {!teams.find((t) => t.name === t2) && <option>{t2}</option>}
            </select>
          </Field>
          <button onClick={applyPairing} data-testid="apply-pairing-btn" className="bg-[#E5FF00] text-black font-display font-bold uppercase tracking-wider text-sm px-5 py-2.5 rounded-lg hover:bg-white transition-colors">
            Übernehmen
          </button>
        </div>
      </section>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Score */}
        <section className="bg-[#121620] border border-[#1E2638] rounded-2xl p-6">
          <h2 className="font-display font-bold uppercase tracking-wider text-lg mb-4">Spielstand</h2>
          <div className="grid grid-cols-2 gap-4">
            <ScorePanel name={live.team1_name} color="#0088FF" value={live.score1} onInc={() => score("team1", 1)} onDec={() => score("team1", -1)} testid="team1" />
            <ScorePanel name={live.team2_name} color="#FF6600" value={live.score2} onInc={() => score("team2", 1)} onDec={() => score("team2", -1)} testid="team2" />
          </div>
        </section>

        {/* Cards */}
        <section className="bg-[#121620] border border-[#1E2638] rounded-2xl p-6">
          <h2 className="font-display font-bold uppercase tracking-wider text-lg mb-4">Karten / Strafen</h2>
          <div className="grid grid-cols-2 gap-6">
            {["team1", "team2"].map((tk) => (
              <div key={tk} className="space-y-3">
                <p className="font-display font-bold uppercase tracking-wide text-sm" style={{ color: tk === "team1" ? "#0088FF" : "#FF6600" }}>
                  {tk === "team1" ? live.team1_name : live.team2_name}
                </p>
                <CardRow label="Gelb" color="#FFD700" value={live.cards[tk].yellow} onInc={() => card(tk, "yellow", 1)} onDec={() => card(tk, "yellow", -1)} testid={`${tk}-yellow`} />
                <CardRow label="Rot" color="#FF2A55" value={live.cards[tk].red} onInc={() => card(tk, "red", 1)} onDec={() => card(tk, "red", -1)} testid={`${tk}-red`} />
              </div>
            ))}
          </div>
        </section>
      </div>

      {/* Timer */}
      {showTimer && (
      <section className="bg-[#121620] border border-[#1E2638] rounded-2xl p-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="font-display font-bold uppercase tracking-wider text-lg">Zeiterfassung</h2>
          <span className="font-display uppercase tracking-widest text-sm text-[#00F0FF]" data-testid="timer-half-label">
            {phase === "halftime" ? "Halbzeit" : phase === "ended" ? "Spielende" : `${half}. Halbzeit`}
          </span>
        </div>
        <div className="flex flex-wrap items-center gap-6">
          <div className="text-center">
            <div className="font-mono-num font-extrabold text-7xl text-[#E5FF00]" data-testid="admin-clock">{clock}</div>
            {parts.injury > 0 && phase === "playing" && (
              <div className="font-display uppercase tracking-widest text-xl text-[#00F0FF]" data-testid="admin-injury">+{formatClock(parts.injury)}</div>
            )}
          </div>
          <div className="flex flex-wrap gap-2">
            <IconBtn onClick={() => timer("start")} testid="timer-start" className="bg-[#E5FF00] text-black hover:bg-white"><Play className="w-5 h-5" /></IconBtn>
            <IconBtn onClick={() => timer("pause")} testid="timer-pause" className="bg-[#121620] border border-[#1E2638] hover:border-[#37456b]"><Pause className="w-5 h-5" /></IconBtn>
            <IconBtn onClick={() => timer("reset")} testid="timer-reset" className="bg-[#121620] border border-[#1E2638] hover:border-[#37456b]"><RotateCcw className="w-5 h-5" /></IconBtn>
            {phase === "playing" && half === 1 && (
              <button onClick={halfEnd} data-testid="half-end-btn" className="bg-[#FF6600] text-black font-display font-bold uppercase tracking-wider text-sm px-4 py-2.5 rounded-lg hover:bg-white transition-colors">Halbzeit</button>
            )}
            {phase === "halftime" && (
              <button onClick={halfStart} data-testid="half-start-btn" className="bg-[#00F0FF] text-black font-display font-bold uppercase tracking-wider text-sm px-4 py-2.5 rounded-lg hover:bg-white transition-colors">2. Halbzeit starten</button>
            )}
          </div>
          <div className="flex items-end gap-2">
            <Field label="Modus">
              <select value={timerMode} onChange={(e) => setTimerMode(e.target.value)} data-testid="timer-mode" className="input">
                <option value="countdown">Countdown</option>
                <option value="countup">Countup</option>
              </select>
            </Field>
            {timerMode === "countdown" && (
              <>
                <Field label="1. HZ (Min)">
                  <input type="number" value={h1Min} onChange={(e) => setH1Min(Number(e.target.value))} data-testid="timer-half1" className="input w-20" />
                </Field>
                <Field label="2. HZ (Min)">
                  <input type="number" value={h2Min} onChange={(e) => setH2Min(Number(e.target.value))} data-testid="timer-half2" className="input w-20" />
                </Field>
              </>
            )}
            <button onClick={configTimer} data-testid="timer-config-btn" className="bg-[#00F0FF] text-black font-display font-bold uppercase tracking-wider text-sm px-4 py-2.5 rounded-lg hover:bg-white transition-colors">Setzen</button>
          </div>
          <div className="flex items-end gap-2">
            <Field label="Nachspielzeit (+Min)">
              <input type="number" value={injury} onChange={(e) => setInjury(e.target.value)} data-testid="injury-input" className="input w-24" />
            </Field>
            <button onClick={saveInjury} data-testid="injury-btn" className="bg-[#121620] border border-[#1E2638] hover:border-[#37456b] font-display font-bold uppercase tracking-wider text-sm px-4 py-2.5 rounded-lg">+</button>
          </div>
        </div>
        <p className="text-xs text-[#8A99AD] mt-3">Shortcuts: Leertaste = Start/Pause · Q/A = Tor Team1 · P/L = Tor Team2</p>
      </section>
      )}

      {!showTimer && (
        <section className="bg-[#121620] border border-[#1E2638] rounded-2xl p-6">
          <h2 className="font-display font-bold uppercase tracking-wider text-lg mb-1">Spielphase</h2>
          <p className="text-sm text-[#8A99AD] mb-4">Zeitlos-Modus (keine Uhr). Die gewählte Phase erscheint unter dem Spielstand auf der Anzeigetafel. Ein-/Ausblenden nur über den Live-Button oben.</p>
          <div className="flex flex-wrap gap-2">
            <PhaseBtn active={phase === "playing" && half === 1} onClick={() => setPhase("first")} testid="phase-first">1. Halbzeit</PhaseBtn>
            <PhaseBtn active={phase === "halftime"} onClick={() => setPhase("halftime")} testid="phase-halftime">Halbzeit</PhaseBtn>
            <PhaseBtn active={phase === "playing" && half === 2} onClick={() => setPhase("second")} testid="phase-second">2. Halbzeit</PhaseBtn>
            <PhaseBtn active={phase === "ended"} onClick={() => setPhase("ended")} testid="phase-ended">Spiel beendet</PhaseBtn>
          </div>
        </section>
      )}

      {/* Finish */}
      <section className="bg-[#121620] border border-[#1E2638] rounded-2xl p-6 flex flex-wrap gap-3">
        <button onClick={endMatch} data-testid="end-match-btn" className="flex items-center gap-2 bg-[#FF2A55] text-white font-display font-bold uppercase tracking-wider px-6 py-3 rounded-lg hover:bg-white hover:text-black transition-colors">
          <Flag className="w-5 h-5" /> Spielende
        </button>
        <button onClick={pushTable} data-testid="push-table-btn" className="flex items-center gap-2 bg-[#E5FF00] text-black font-display font-bold uppercase tracking-wider px-6 py-3 rounded-lg hover:bg-white transition-colors">
          <ArrowRightToLine className="w-5 h-5" /> In Tabelle übernehmen
        </button>
        <span className="ml-auto self-center font-display uppercase tracking-widest text-sm text-[#8A99AD]">
          Status: <span className="text-[#00F0FF]">{live.status}</span>
        </span>
      </section>
    </div>
  );
}

const Field = ({ label, children }) => (
  <label className="flex flex-col gap-1">
    <span className="text-xs uppercase tracking-widest text-[#8A99AD]">{label}</span>
    {children}
  </label>
);

const IconBtn = ({ onClick, children, className, testid }) => (
  <button onClick={onClick} data-testid={testid} className={`p-3 rounded-lg transition-colors ${className}`}>
    {children}
  </button>
);

const PhaseBtn = ({ active, onClick, children, testid }) => (
  <button onClick={onClick} data-testid={testid}
    className={`font-display font-bold uppercase tracking-wider text-sm px-5 py-3 rounded-lg transition-colors ${active ? "bg-[#E5FF00] text-black" : "bg-[#0A0C10] border border-[#1E2638] text-white hover:border-[#37456b]"}`}>
    {children}
  </button>
);

function ScorePanel({ name, color, value, onInc, onDec, testid }) {
  return (
    <div className="rounded-xl border border-[#1E2638] p-4 text-center">
      <p className="font-display font-bold uppercase tracking-wide text-sm truncate" style={{ color }}>{name}</p>
      <div className="font-mono-num font-extrabold text-7xl my-2" data-testid={`score-${testid}`}>{value}</div>
      <div className="flex gap-2 justify-center">
        <button onClick={onInc} data-testid={`score-${testid}-inc`} className="flex-1 bg-[#E5FF00] text-black font-bold py-2 rounded-lg hover:bg-white transition-colors flex items-center justify-center"><Plus className="w-5 h-5" /></button>
        <button onClick={onDec} data-testid={`score-${testid}-dec`} className="flex-1 bg-[#0A0C10] border border-[#1E2638] py-2 rounded-lg hover:border-[#37456b] transition-colors flex items-center justify-center"><Minus className="w-5 h-5" /></button>
      </div>
    </div>
  );
}

function CardRow({ label, color, value, onInc, onDec, testid }) {
  return (
    <div className="flex items-center gap-3">
      <div className="w-5 h-7 rounded-[3px]" style={{ background: color }} />
      <span className="text-sm text-[#8A99AD] w-10">{label}</span>
      <span className="font-mono-num font-bold text-2xl w-8 text-center" data-testid={`card-${testid}`}>{value}</span>
      <button onClick={onInc} data-testid={`card-${testid}-inc`} className="p-1.5 rounded bg-[#0A0C10] border border-[#1E2638] hover:border-[#37456b]"><Plus className="w-4 h-4" /></button>
      <button onClick={onDec} data-testid={`card-${testid}-dec`} className="p-1.5 rounded bg-[#0A0C10] border border-[#1E2638] hover:border-[#37456b]"><Minus className="w-4 h-4" /></button>
    </div>
  );
}
